import { Box } from "@mui/material"
import { useNavigate } from "@tanstack/react-router"
import type { FC } from "react"
import { useState } from "react"
import { PRODUCT_TEST_RESULTS_ROUTE_PATH } from "routes/app/product/$productId/edit/_layout/quality/_layout/results.route"

import { useQualityRequestList } from "../hooks/useQualityRequestList"

import { DashboardGreeting } from "./DashboardGreeting"
import { QualityRequestTable } from "./QualityRequestTable"

import SearchInput from "@/components/SearchInput/SearchInput"
import { AerosSelect } from "@/components/UiKit"
import {
  ProductTestResultStatus,
  type QualityApproval,
  QualityApprovalRequestStatusFilter,
} from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { QualityApprovalCancelDialog } from "@/screens/Products/components/Product/Quality/TestResults/components/QualityApprovalCancelDialog"
import { useCancelQualityApproval } from "@/screens/Products/components/Product/Quality/TestResults/hooks/useCancelQualityApproval"
import { useCreateQualityApproval } from "@/screens/Products/components/Product/Quality/TestResults/hooks/useCreateQualityApproval"
import { useNotificationsStore } from "@/stores/useNotificationsStore"
import type { Option } from "@/types/form-fields"

const STATUS_FILTER_OPTIONS: Option<QualityApprovalRequestStatusFilter>[] = [
  { value: QualityApprovalRequestStatusFilter.All, label: "All" },
  { value: QualityApprovalRequestStatusFilter.Pending, label: "Pending" },
  { value: QualityApprovalRequestStatusFilter.Approved, label: "Approved" },
  { value: QualityApprovalRequestStatusFilter.Denied, label: "Denied" },
]

export const MyRequestsPage: FC = () => {
  const navigate = useNavigate()
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [cancelingQualityApproval, setCancelingQualityApproval] = useState<QualityApproval | undefined>()

  const {
    qualityRequests,
    isFetching,
    error,
    sorting,
    searchParams,
    tableContainerRef,
    refetch,
    handleScroll,
    handleSortingChange,
    handleSearchChange,
    handleFilterChange,
  } = useQualityRequestList()

  const { createQualityApproval } = useCreateQualityApproval()
  const { cancelQualityApproval } = useCancelQualityApproval()

  const handleProductView = (qualityApproval: QualityApproval) => {
    const productTest = qualityApproval.productTestResult?.productTest
    const productId = productTest?.product?.productId

    if (!productId) return

    navigate({
      to: PRODUCT_TEST_RESULTS_ROUTE_PATH,
      params: { productId: String(productId) },
      search: { query: productTest?.lotNumber },
    })
  }

  const handleApprovalRequest = async (qualityApproval: QualityApproval) => {
    if (qualityApproval.productTestResult?.status !== ProductTestResultStatus.ResultsNotWithinSpec) {
      notify({
        type: NotificationType.Error,
        message: "This Test Result is no longer Out of Spec and can't be requested for approval",
      })
      return
    }

    const isSuccess = await createQualityApproval({ productTestResultId: qualityApproval.productTestResultId })

    if (isSuccess) refetch()
  }

  const handleApprovalCancelConfirm = async () => {
    if (!cancelingQualityApproval) return

    const isSuccess = await cancelQualityApproval({ qualityApprovalId: cancelingQualityApproval.qualityApprovalId })
    setCancelingQualityApproval(undefined)

    if (isSuccess) refetch()
  }

  return (
    <Box className='flex min-h-0 flex-1 flex-col gap-y-6' data-testid='dashboard.my-requests-page'>
      <DashboardGreeting message="Here's the status of your requests" />
      <Box className='flex items-end gap-x-3'>
        <SearchInput
          placeholder='Search by Product ID, Description, Lot, Supplier, or Test Description'
          defaultValue={searchParams.query}
          onChange={(e) => handleSearchChange(e.target.value)}
          className='w-full max-w-[524px]'
        />
        <AerosSelect<QualityApprovalRequestStatusFilter>
          label='Status Filter'
          options={STATUS_FILTER_OPTIONS}
          value={searchParams.status}
          onChange={(value) => handleFilterChange("status", value ?? QualityApprovalRequestStatusFilter.All)}
          size='small'
          className='w-[238px]'
          data-testid='dashboard.my-requests-page.status-filter-select'
        />
      </Box>
      <Box className='flex min-h-0 flex-1 overflow-y-auto'>
        <QualityRequestTable
          data={qualityRequests}
          loading={isFetching}
          error={error}
          sorting={sorting}
          containerRef={tableContainerRef}
          onScroll={handleScroll}
          onSortingChange={handleSortingChange}
          onProductView={handleProductView}
          onApprovalRequest={handleApprovalRequest}
          onApprovalCancel={setCancelingQualityApproval}
        />
      </Box>
      {cancelingQualityApproval && (
        <QualityApprovalCancelDialog
          onClose={() => setCancelingQualityApproval(undefined)}
          onConfirm={handleApprovalCancelConfirm}
        />
      )}
    </Box>
  )
}
